import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IntlProvider } from 'react-intl';
import { get, isEmpty, isEqual } from 'lodash';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import {
  extractWhitelabelSubdomain,
  getAppInStartupPhase,
  setIsApiRequestOnHold,
} from '../../utils/helpers';
import { getSecondaryPathName } from '../../selectors';
import routes from '../../routes';
import rewardRoutes from '../../routes/rewards';
import ThemeContext from '../ThemeContext';
import DefaultRegularFont from '../../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../../assets/fonts/OpenSans-Bold.ttf';
import resourcesConfig from '../../resources';
import { useTheme } from '../../utils/customHooks';

import {
  fetchAppStartupInfoAction,
  fetchBrandResourcesAction,
  dispatchAppResolutionContext,
  clearApiErrorResponseAction,
  dispatchLoginRequiredBeforeOrderingAction,
} from '../../actions/common';
import { dispatchLogoutAction } from '../../actions/authentication';
import { serverErrorCodes } from '../../constants/globals';

const Instagram = asyncRoute(() => import('./Instagram'));
const SessionExpiredPopover = asyncRoute(() =>
  import('../Rewards/components/SessionExpiredPopover'),
);

const View = ({
  match,
  actions,
  themeData,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
  location,
  history,
  resources,
  errorResponse,
  guestDetails,
}) => {
  const theme = useTheme('rewards-container', themeData);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  useEffect(() => {
    const subDomain = extractWhitelabelSubdomain();
    if (subDomain) {
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['reward', 'brand', 'resource'],
      };
      actions.fetchAppStartupInfo({ appStartupReqBody }).then((appStartupInfoAPIResponse) => {
        const pathname = get(location, 'pathname', '');
        const secondaryPathname = getSecondaryPathName(pathname);
        if (!isEmpty(secondaryPathname)) {
          const appStartupInfo = get(appStartupInfoAPIResponse, 'data', {});
          const outsideAppOrderFlowResolutionType = get(
            appStartupInfo,
            'brandResource.resource.outsideAppOrderFlowResolution',
            [],
          );
          if (!isEmpty(outsideAppOrderFlowResolutionType)) {
            const orderFlowResolutionType = get(
              outsideAppOrderFlowResolutionType.find((item) => item.context === secondaryPathname),
              'type',
              '',
            );

            const isLoginRequiredBeforeOrdering = get(
              outsideAppOrderFlowResolutionType.find((item) => item.context === secondaryPathname),
              'isLoginRequiredBeforeOrdering',
              false,
            );

            orderFlowResolutionType &&
              actions.dispatchAppResolutionContext(orderFlowResolutionType);

            actions.dispatchLoginRequiredBeforeOrdering(isLoginRequiredBeforeOrdering);
          }
        }
      });
      actions.fetchBrandResources({ brandId: subDomain });
    }
  }, []);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  useEffect(() => {
    if (
      !isEmpty(errorResponse) &&
      isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)
    ) {
      handleSessionExpiredErrorResponse(errorResponse);
    }
  }, [errorResponse]);

  const handleSessionExpiredErrorResponse = (errorResponse) => {
    setIsApiRequestOnHold(false);
    const isAppInStartupPhase = getAppInStartupPhase();
    if (isAppInStartupPhase) {
      const prefilledData = {
        primaryPhoneNumber: get(guestDetails, 'primaryMobileNumber'),
        errorResponse,
      };
      handleLogout({ prefilledData });
      actions.clearApiErrorResponse();
    } else {
      setShowSessionExpired(true);
    }
  };

  const handleCloseSessionExpired = () => {
    setShowSessionExpired(false);
    actions.clearApiErrorResponse();
    const prefilledData = {
      primaryPhoneNumber: get(guestDetails, 'primaryMobileNumber'),
      errorResponse,
    };
    handleLogout({ prefilledData });
  };

  const handleLogout = ({ prefilledData }) => {
    localStorage.setItem('GuestLoginData', JSON.stringify({}));
    localStorage.setItem('AuthData', JSON.stringify({}));
    actions.dispatchLogout();
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('view', 'pre-signin'); // Update the 'view' parameter
    // Replace the URL without reloading the page
    history.replace(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { prefilledData },
    );
  };

  return (
    <ThemeContext.Provider value={{ themeContext: rewardRoutes.rewards }}>
      <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
        <div id="rewards-container" className="rewards">
          <Switch location={location}>
            <Route exact path={`${match.path}/${routes.view.instagram}`} component={Instagram} />
          </Switch>
          {showSessionExpired && (
            <SessionExpiredPopover
              open={showSessionExpired}
              onCTAClick={handleCloseSessionExpired}
            />
          )}
        </div>
      </IntlProvider>
    </ThemeContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  resources: get(state, 'common.resources', resourcesConfig),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
  errorResponse: get(state, 'common.errorResponse', {}),
  guestDetails: get(state, 'rewards.rewardGuestDetails', {}),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      fetchBrandResources: fetchBrandResourcesAction,
      dispatchAppResolutionContext,
      dispatchLogout: dispatchLogoutAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
      dispatchLoginRequiredBeforeOrdering: dispatchLoginRequiredBeforeOrderingAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(View);
